import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight } from 'lucide-react'
import { leadsAPI } from './api'
import Pagination from './Pagination'
import type { ScoreRow, ScoreTotals, ScoresParams, UnderenhetMatch } from './api'
import UnderenhetMatchNotice from './UnderenhetMatchNotice'
import { useBookmarks } from './useBookmarks'
import BookmarkStar from './BookmarkStar'
import { useLatest } from '../Bruktbil/useLatest'
import LeadsTableExport from './LeadsTableExport'
import { LEADS_EXPORT_LIMIT } from './eksportTilgang'
import type { ColumnDefinition } from '../../utils/exportToCsv'

type SortCol = 'total_score' | 'leads_faktor' | 'eligible_car_count' | 'total_vehicles' | 'org_name'
type Sort = { col: SortCol; dir: 'asc' | 'desc' } | null

interface Props {
  page: number
  onPageChange: (page: number) => void
  markedsomradeIds?: string
  search: string
  naceFilter: string
  minScore?: number
  categoryFilter?: string
  mineNaceKoder?: boolean
  vehicleClass?: string
  /** The applied filters, one line each, for the top of the PDF export (#885). */
  exportFilters?: string[]
  /** False while the default markedsområde is still being resolved (#892). */
  scopeReady?: boolean
}

const PAGE_SIZE = 20

type ScoresFilters = Pick<Props, 'markedsomradeIds' | 'search' | 'naceFilter' | 'minScore' | 'categoryFilter' | 'mineNaceKoder' | 'vehicleClass'>

/** The applied search, for one page of the table or for the export (#885). */
function scoresParams(f: ScoresFilters, sort: Sort, limit: number, offset: number): ScoresParams {
  return {
    markedsomrade_ids: f.markedsomradeIds,
    name: f.search || undefined,
    nace_kode: f.mineNaceKoder ? undefined : (f.naceFilter || undefined),
    category_code: f.categoryFilter || undefined,
    vehicle_class: f.vehicleClass || undefined,
    mine_nace_koder: f.mineNaceKoder || undefined,
    min_score: f.minScore,
    sort_by: sort ? sort.col : 'total_score',
    limit,
    offset,
  }
}

function SortIcon({ col, sort }: { col: SortCol; sort: Sort }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  return sort.dir === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

/** Grønt, gult og grått. Grensene er tersilene i faktorfordelingen — p33 er
 *  4,17 og p66 er 10,00 blant selskaper med faktor over 0.
 *
 *  Satt på snittet (9,3) traff de p66 og p90, så bare hver tiende rad kunne bli
 *  grønn. Lista sorteres på Score, som løfter de største og dermed kaldeste
 *  flåtene øverst — øverste selskap har 9 413 biler og faktor 4,36 — så
 *  førstesiden ble nesten helt grå. */
function faktorKlasse(faktor: number): string {
  if (faktor >= 10) return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  if (faktor >= 4) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
  return 'text-gray-600 dark:text-gray-400'
}

export default function BedrifterTab({ page, onPageChange, markedsomradeIds, search, naceFilter, minScore, categoryFilter, mineNaceKoder, vehicleClass, exportFilters, scopeReady = true }: Props) {
  const { t } = useTranslation('leads')
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState<ScoreRow[]>([])
  const [totals, setTotals] = useState<ScoreTotals | null>(null)
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [underenhetMatch, setUnderenhetMatch] = useState<UnderenhetMatch | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sort, setSort] = useState<Sort>({ col: 'total_score', dir: 'desc' })
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()

  // Reset page when filters change
  useEffect(() => { onPageChange(1) }, [markedsomradeIds, search, naceFilter, minScore, categoryFilter, mineNaceKoder, vehicleClass])

  const filters: ScoresFilters = { markedsomradeIds, search, naceFilter, minScore, categoryFilter, mineNaceKoder, vehicleClass }

  // useLatest, not useEffect: a superseded answer must not land. The unscoped
  // query is the slowest, so it used to arrive after the markedsområde one and
  // replace the list with every postnummer (#892).
  useLatest(alive => {
    setLoading(true)
    setError(null)
    // Until the default markedsområde is known, any query would be the wrong one.
    if (!scopeReady) return
    leadsAPI.getScores(scoresParams(
      { markedsomradeIds, search, naceFilter, minScore, categoryFilter, mineNaceKoder, vehicleClass },
      sort, PAGE_SIZE, (page - 1) * PAGE_SIZE,
    ))
      .then(res => {
        if (!alive()) return
        setRows(res.data.items)
        setTotals(res.data.totals ?? null)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
        setUnderenhetMatch(res.data.underenhet_match ?? null)
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [scopeReady, markedsomradeIds, search, naceFilter, minScore, categoryFilter, mineNaceKoder, vehicleClass, sort, page])

  const exportColumns: ColumnDefinition[] = [
    { key: 'navn', label: t('common.bedrift'), format: v => v ?? '—' },
    { key: 'org_nr', label: t('common.orgNr') },
    { key: 'nace_kode', label: t('common.nace'), format: v => v ?? '—' },
    { key: 'nace_beskrivelse', label: t('common.naceNavn'), format: v => v ?? '—' },
    { key: 'total_score', label: t('common.score') },
    { key: 'leads_faktor', label: t('bedrifter.faktor'), format: v => (v ? Number(v).toLocaleString('nb-NO', { maximumFractionDigits: 2 }) : '—') },
    { key: 'total_vehicles', label: t('common.biler'), format: v => String(v ?? 0) },
    { key: 'eligible_car_count', label: t('common.leads') },
    { key: 'hottest_category', label: t('common.kategori'), format: v => v ?? '—' },
    { key: 'kommune', label: t('common.kommune'), format: v => v ?? '—' },
  ]

  function handleSort(col: SortCol) {
    setSort(s => {
      if (s?.col === col) return s.dir === 'desc' ? { col, dir: 'asc' } : null
      return { col, dir: 'desc' }
    })
  }


  if (error) {
    return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>
  }

  return (
    <div className="card p-6">
      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
          <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {t('bedrifter.loading')}
        </div>
      )}

      {!loading && (
        <>
          <UnderenhetMatchNotice match={underenhetMatch} />
          <LeadsTableExport
            rows={rows}
            columns={exportColumns}
            title={t('tableExport.bedrifterTitle')}
            filters={exportFilters}
            loadData={() => leadsAPI.getScores(scoresParams(filters, sort, LEADS_EXPORT_LIMIT, 0)).then(res => res.data.items)}
            capped={totalIsCapped}
            testId="bedrifter-export"
          />
          {/* Sikkerhetsnett: raden har mange kolonner, og uten dette dyttet
              tabellen seg utenfor kortet i stedet for å scrolle inni det. */}
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact w-full">
              <thead>
                <tr>
                  <th className="text-center w-8"></th>
                  <th className="text-left whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('org_name')}>
                    {t('common.bedrift')} <SortIcon col="org_name" sort={sort} />
                  </th>
                  <th className="text-left whitespace-nowrap">{t('common.orgNr')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.nace')}</th>
                  <th className="text-left whitespace-nowrap max-w-[140px]">{t('common.naceNavn')}</th>
                  <th className="text-right whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('total_score')}>
                    {t('common.score')} <SortIcon col="total_score" sort={sort} />
                  </th>
                  <th className="text-right whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('leads_faktor')}
                      title={t('bedrifter.faktorTitle')}>
                    {t('bedrifter.faktor')} <SortIcon col="leads_faktor" sort={sort} />
                  </th>
                  <th className="text-right whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('total_vehicles')}>
                    {t('common.biler')} <SortIcon col="total_vehicles" sort={sort} />
                  </th>
                  <th className="text-right whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('eligible_car_count')}>
                    {t('common.leads')} <SortIcon col="eligible_car_count" sort={sort} />
                  </th>
                  <th className="text-left whitespace-nowrap">{t('common.kategori')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.kommune')}</th>
                  <th className="text-center whitespace-nowrap">{t('common.details')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Totalrad over hele trefflista, ikke bare denne siden (#416).
                    Under Kjøretøytype-filteret summerer den klassens lagrede
                    kolonner (#627 AC1), og den vises også med 0 treff. */}
                {totals && (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 border-b-2 border-primary-200 dark:border-primary-800 font-bold">
                    <td></td>
                    <td colSpan={6} className="whitespace-nowrap">
                      {t('bedrifter.total')}
                      <span className="ml-2 font-normal text-xs text-gray-600 dark:text-gray-400">
                        {t('bedrifter.orgCount', { n: totals.org_count.toLocaleString('nb-NO') })}
                      </span>
                    </td>
                    <td className="text-right tabular-nums">{totals.total_vehicles.toLocaleString('nb-NO')}</td>
                    <td className="text-right tabular-nums">{totals.eligible_car_count.toLocaleString('nb-NO')}</td>
                    <td colSpan={3}></td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr
                    key={row.org_nr}
                    className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}
                  >
                    <td className="text-center text-lg">
                      <BookmarkStar active={isBookmarked(row.org_nr)} onToggle={() => toggleBookmark(row.org_nr)} />
                    </td>
                    {/* Firmanavn er den lengste frie teksten i raden. max-width
                        på en td ignoreres i auto table-layout, så klippingen må
                        skje på et element inni cella. */}
                    <td className="font-bold">
                      <div className="max-w-[180px] truncate" title={row.navn ?? ''}>{row.navn ?? '—'}</div>
                    </td>
                    <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.org_nr}</td>
                    <td className="font-mono text-xs">{row.nace_kode ?? '—'}</td>
                    <td className="text-xs text-gray-500 max-w-[140px] truncate" title={row.nace_beskrivelse ?? ''}>{row.nace_beskrivelse ?? '—'}</td>
                    <td className="text-right font-bold">{row.total_score}</td>
                    <td className="text-right">
                      {(row.leads_faktor ?? 0) > 0 ? (
                        <span
                          className={`inline-flex items-center justify-end px-2 py-0.5 rounded-sm text-xs font-bold tabular-nums ${faktorKlasse(row.leads_faktor)}`}
                          title={t('bedrifter.faktorTooltip', { faktor: row.leads_faktor.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), grunnlag: (row.leads_faktor_grunnlag ?? 0).toLocaleString('nb-NO') })}
                        >
                          {row.leads_faktor.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="text-right">{(row.total_vehicles ?? 0).toLocaleString('nb-NO')}</td>
                    <td className="text-right">{row.eligible_car_count}</td>
                    <td>
                      {row.hottest_category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                          {row.hottest_category}
                        </span>
                      )}
                    </td>
                    <td className="text-sm">{row.kommune ?? '—'}</td>
                    <td className="text-center">
                      <button
                        onClick={() => navigate(`/leads/bedrift/${row.org_nr}`, { state: { from: location.pathname + location.search } })}
                        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        <span className="text-xs font-medium">{t('common.see')}</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('bedrifter.empty')}</p>
          )}

          <Pagination
            page={page}
            onPageChange={onPageChange}
            total={total}
            totalIsCapped={totalIsCapped}
            hasMore={hasMore}
            pageSize={PAGE_SIZE}
          />
        </>
      )}
    </div>
  )
}
